import React, { useMemo } from "react";

import AccordionItem from "../custom-visual-inspection/accordion-item";
import Conditional from "../../common/conditional";
import CustomAnomalyItems from "../custom-general/custom-anomaly-items";
import PDOAnomalyForm from "../../forms/custom-pdo/pdoAnomalyForm";
import Zippy from "../../themed/zippy";
import VIPhotoRecordForm from "../../forms/visual-inspection/viPhotoRecordForm";

import { CoverDataItem, DamageClassItem, NewAnomalyItem, PDFImageProps } from "../../../utils/models";
import { getAnomalyStartNumber, matchPhrases } from "../../../utils/utilities";

interface Props {
    title: string;
    damageClasses: DamageClassItem[];
    locations: CoverDataItem[];
    checklistItems: PDOChecklistItem[];
    onChecklistItemsChange: (items: PDOChecklistItem[]) => void;
}

export interface PDOChecklistItem {
    label: string;
    hasAnomalies: 'yes' | 'no' | '';
    generalPhotos: PDFImageProps[];
    anomalyItems: NewAnomalyItem[];
}

const PDOCheckList: React.FC<Props> = ({ checklistItems, damageClasses, locations, onChecklistItemsChange, title }) => {
    const handleUpdateGeneralChecklistOption = (checklistItem: PDOChecklistItem, hasAnomalies: PDOChecklistItem['hasAnomalies']) => {
        const updatedGeneralChecklistItems = checklistItems.map((item) => {
            if (item.label === checklistItem.label) {
                return {
                    label: checklistItem.label,
                    hasAnomalies: hasAnomalies,
                    anomalyItems: checklistItem.anomalyItems,
                    generalPhotos: checklistItem.generalPhotos,
                };
            }

            return item;
        });

        onChecklistItemsChange(updatedGeneralChecklistItems);
    };
    
    const handleAnomalyItemsUpdate = (checklistItem: PDOChecklistItem, anomalyItems: NewAnomalyItem[]) => {
        const updatedGeneralChecklistItems = checklistItems.map((item) => {
            if (item.label === checklistItem.label) {
                return {
                    label: checklistItem.label,
                    hasAnomalies: checklistItem.hasAnomalies,
                    anomalyItems: anomalyItems,
                    generalPhotos: checklistItem.generalPhotos,
                };
            }

            return item;
        });

        onChecklistItemsChange(updatedGeneralChecklistItems);
    };

    const handleGeneralPhotosUpdate = (checklistItem: PDOChecklistItem, generalPhotos: PDFImageProps[]) => {
        const updatedGeneralChecklistItems = checklistItems.map((item) => {
            if (item.label === checklistItem.label) {
                return {
                    label: checklistItem.label,
                    hasAnomalies: checklistItem.hasAnomalies,
                    anomalyItems: checklistItem.anomalyItems,
                    generalPhotos: generalPhotos,
                };
            }

            return item;
        });

        onChecklistItemsChange(updatedGeneralChecklistItems);
    };

    const isValid = useMemo(() => {
        return checklistItems.every((checklist) => checklist.hasAnomalies);
    }, [checklistItems]);

    return ( 
        <Zippy headerTitle={title} isValid={isValid}>
            <div className="bg-light border rounded-sm p-2">
                {checklistItems.map((checklist) => (
                    <Zippy key={checklist.label} headerTitle={checklist.label} isValid={!!checklist.hasAnomalies}>
                        <PDOAnomalyForm
                            hasAnomalies={checklist.hasAnomalies}
                            onSave={(hasAnomalies: PDOChecklistItem['hasAnomalies']) => handleUpdateGeneralChecklistOption(checklist, hasAnomalies)}
                        />

                        <Conditional isVisible={matchPhrases(checklist.hasAnomalies, 'yes')}>
                            <AccordionItem isActive title="Anomaly Items">
                                <CustomAnomalyItems 
                                    isPDOFormat={false}
                                    nextAnomalyID={getAnomalyStartNumber(checklistItems)}
                                    locationOptions={locations}
                                    isNewFormat
                                    damageClasses={damageClasses}
                                    items={checklist.anomalyItems}
                                    onDataChange={(items: NewAnomalyItem[]) => handleAnomalyItemsUpdate(checklist, items)}
                                />
                            </AccordionItem>
                        </Conditional>

                        <AccordionItem isActive title="General Photos">
                            <VIPhotoRecordForm
                                initialGeneralPhotos={checklist.generalPhotos}
                                onProceed={(images: PDFImageProps[]) => handleGeneralPhotosUpdate(checklist, images)}
                            />
                        </AccordionItem>
                    </Zippy>
                ))}
            </div>
        </Zippy>
    );
};
 
export default PDOCheckList;